/**
 * The PIN entry control.
 *
 * ## Why a keypad rather than the text field it replaces
 *
 * The field was a `<input type="password" inputMode="numeric">`, which is the right control
 * on exactly one of this app's three devices.
 *
 * On the television it was the wrong control outright. Focusing a text input on Tizen opens
 * the platform's on-screen keyboard — a full QWERTY panel, laid over the app, that the
 * viewer then has to walk with a DPAD to find four digits scattered across the top row.
 * That is roughly twenty presses for a four-digit number, and the keyboard covers the
 * dialog it belongs to while it is up.
 *
 * On a phone `inputMode="numeric"` does summon a numeric keyboard, but it is the system
 * one: it slides half the screen away, it is not this app's colours, and on a locked
 * profile it appears over a dialog that is itself already a bottom sheet.
 *
 * A keypad is the control every other product uses for a PIN, and it is the same control on
 * all three: ten targets, each reachable in one press from the remote, one thumb-sized tap
 * on the phone, and one click on a desktop — where a physical keyboard also works, because
 * digits typed anywhere inside the pad are accepted.
 *
 * ## Auto-submit
 *
 * Filling the last digit submits. A separate "Aç" button after a complete PIN is one more
 * focus move on the device with the most expensive focus moves, and there is nothing to
 * review — a four-digit number is either the one or it is not. A wrong PIN clears the
 * digits and says so, which is the same recovery a confirm button would have given.
 *
 * ## Why no `<input>` at all
 *
 * `AutoFocusables` claims the first focusable inside a dialog, and `Dialog` prefers an
 * `input` over a button when it moves focus on open. With no field present both land on
 * the "1" key, which is where a remote should start. A hidden input would have re-created
 * the Tizen keyboard problem the moment anything focused it.
 */
import { useEffect, useRef } from 'react';
import { Icon } from './Icon';

/** 1–9, then clear / 0 / backspace. `null` is drawn as an empty cell. */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'] as const;

export function PinPad({
  length,
  value,
  onChange,
  onComplete,
  disabled,
  label = 'PIN',
}: {
  length: number;
  value: string;
  onChange: (next: string) => void;
  /** Called with the full PIN the moment the last digit lands. */
  onComplete: (pin: string) => void;
  disabled?: boolean;
  label?: string;
}) {
  /*
   * `onComplete` through a ref.
   *
   * The caller's handler closes over its own state (the profile being unlocked, the busy
   * flag) and is therefore a new function every render. Calling it directly from the key
   * handler is fine, but the physical-keyboard listener below would have to re-subscribe on
   * every keystroke — and a listener that is torn down and rebuilt while a key is being
   * held is how a repeat gets dropped.
   */
  const complete = useRef(onComplete);
  useEffect(() => {
    complete.current = onComplete;
  }, [onComplete]);

  const push = (digit: string) => {
    if (disabled || value.length >= length) return;
    const next = value + digit;
    onChange(next);
    if (next.length === length) complete.current(next);
  };

  const press = (key: (typeof KEYS)[number]) => {
    if (key === 'clear') onChange('');
    else if (key === 'back') onChange(value.slice(0, -1));
    else push(key);
  };

  /*
   * A physical keyboard still works, on the element rather than the document.
   *
   * `Dialog` moves focus into the box on open, so the pad always has focus somewhere inside
   * it while it is on screen and a bubbled listener sees every key. A `document` listener
   * would have been simpler and wrong: two profile prompts can never be open at once today,
   * but a global digit handler is the kind of thing that outlives the screen that added it.
   *
   * Escape and Tab are deliberately not touched — `Dialog` owns both, and this handler runs
   * first only because it is deeper in the tree.
   */
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      push(event.key);
    } else if (event.key === 'Backspace') {
      event.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="pinpad" onKeyDown={onKeyDown}>
      {/*
        The dots are a picture of the length, not a field.

        `aria-live` on the count rather than on the dots: announcing "filled, filled, empty,
        empty" on every press is noise, and reading the digits back aloud would be worse
        than noise on a shared television.
      */}
      <div className="pinpad__dots" aria-hidden>
        {Array.from({ length }, (_, i) => (
          <span key={i} className={`pinpad__dot${i < value.length ? ' is-filled' : ''}`} />
        ))}
      </div>
      <p className="sr-only" aria-live="polite">
        {`${label}: ${value.length} / ${length} rakam girildi`}
      </p>

      <div className="pinpad__keys">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={`pinpad__key${key === 'clear' || key === 'back' ? ' pinpad__key--aux' : ''}`}
            // Disabled keys leave the focus registry entirely — `AutoFocusables` skips
            // `[disabled]` — so a pad that greys out mid-unlock would drop the remote's
            // focus onto nothing. Only the two auxiliaries can be genuinely empty-handed.
            disabled={disabled || (key === 'back' && value.length === 0)}
            aria-label={key === 'clear' ? 'Temizle' : key === 'back' ? 'Sil' : key}
            onClick={() => press(key)}
          >
            {key === 'clear' ? 'C' : key === 'back' ? <Icon name="back" size={1.1} /> : key}
          </button>
        ))}
      </div>
    </div>
  );
}
